/**
 * MessagingLibrary — the Messages tab of the Messaging rail. A library of
 * channel-complete messages with status sub-tabs, list/grid views, per-message
 * actions — and, since 2026-08-04 (operator), FOLDER ORGANIZATION: a reusable
 * label-based folder system (collections-api + collections-ui) with a desktop
 * tree panel, folder cards on phone/tablet, drag-and-drop filing, multi-select
 * bulk moves, folder-scoped tabs/search (one click to widen), and ask-each-time
 * folder deletion (keep vs purge). Surveys live ONLY in the Surveys tool.
 *
 * Route: `messaging` · rail tab: Messages.
 */
import React, { useEffect, useState } from "react";
import { Btn, Card, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { ExplainerFooter, EmptyState, LoadState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useViewport } from "../../shell/useResponsive";
import { ChGlyph, ChannelChip, MSG_CHANNEL, msgStatusPill } from "./messaging-shared";
import { useMessaging } from "./useMessaging";
import { AddToFlowModal } from "./MessageComposer";
import { useFlows } from "./useFlows";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import { useCollections, type Folder } from "../../lib/collections-api";
import { FolderTree, FolderCards, FolderBreadcrumb, FolderPickerPopover, DeleteFolderModal, type FolderScope } from "../shared/collections-ui";
import { useT, t as i18nT } from "../../lib/i18n";

const msgTh = { textAlign: "left" as const, padding: "10px 16px", whiteSpace: "nowrap" as const };
const msgTd = { padding: "12px 16px", verticalAlign: "middle" as const };

interface Row {
  id: string; name: string; status: string;
  channels: string[]; updated: string; sentLabel?: string;
}

export default function MessagingLibrary() {
  const t = useT();
  const nav = useNav();
  const store = useMessaging();
  useFlows(); // warm the flows cache so the rail tab count is live
  const counts = useMessagingCounts();
  const [tab, setTab] = useState("all");
  const [view, setView] = useViewMode("messaging:view");
  const [q, setQ] = useState("");
  const desktop = useViewport() === "desktop";

  const col = useCollections("messages");
  const [scope, setScope] = useState<FolderScope>("all");
  const [wide, setWide] = useState(false); // search-all override while scoped
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [labelFor, setLabelFor] = useState<{ id: string; x: number; y: number } | null>(null);
  const [moveAt, setMoveAt] = useState<{ x: number; y: number } | null>(null);
  const [askDelete, setAskDelete] = useState<Folder | null>(null);
  const [delBusy, setDelBusy] = useState(false);

  // A folder that vanished (tenant switch, deletion elsewhere) must not strand the view.
  useEffect(() => {
    if (scope !== "all" && scope !== "unfiled" && !col.loading && !col.folders.some((f) => f.id === scope)) setScope("all");
  }, [scope, col.loading, col.folders]);
  useEffect(() => { setWide(false); setSel(new Set()); }, [scope]);
  // Widening is a property of the CURRENT search — clearing the query snaps back to the folder
  // (verifier: search→widen→clear left the tabs saying 13 while the list showed all 60).
  useEffect(() => { if (!q.trim()) setWide(false); }, [q]);

  const canWrite = can("campaigns.write");
  const msgRows: Row[] = store.all().map((m) => ({ id: m.id, name: m.name, status: m.status, channels: m.channels, updated: m.updated, sentLabel: m.sentLabel }));

  const filedIds = new Set<string>();
  Object.values(col.members).forEach((arr) => arr.forEach((id) => filedIds.add(id)));
  const inScope = (id: string): boolean =>
    scope === "all" ? true : scope === "unfiled" ? !filedIds.has(id) : col.rollup(scope).has(id);
  const scoped = msgRows.filter((m) => inScope(m.id));
  const searchPool = wide ? msgRows : scoped;
  const tabCounts = { all: scoped.length, draft: scoped.filter((m) => m.status === "draft").length, live: scoped.filter((m) => m.status === "live").length };
  const rows = searchPool.filter((m) => (tab === "all" || m.status === tab) && (!q.trim() || m.name.toLowerCase().includes(q.toLowerCase())));

  const countFor = (s: FolderScope): number =>
    s === "all" ? msgRows.length : s === "unfiled" ? msgRows.filter((m) => !filedIds.has(m.id)).length : msgRows.filter((m) => col.rollup(String(s)).has(m.id)).length;

  /* ── filing operations ── */
  const scopedFolder = scope !== "all" && scope !== "unfiled" ? scope : null;
  const dropIds = async (target: FolderScope, ids: string[]) => {
    try {
      if (target === "unfiled") {
        // Strip every label from these items.
        const byFolder = new Map<string, string[]>();
        ids.forEach((id) => col.foldersOf(id).forEach((fid) => byFolder.set(fid, [...(byFolder.get(fid) || []), id])));
        for (const [fid, list] of byFolder) await col.setItems(fid, { remove: list });
        toast({ tone: "success", title: t("collections.movedToast", "Moved"), subtitle: `${ids.length} → ${t("collections.unfiled", "Unfiled")}` });
      } else {
        await col.setItems(String(target), { add: ids });
        if (scopedFolder && scopedFolder !== target) await col.setItems(scopedFolder, { remove: ids });
        const f = col.folders.find((x) => x.id === target);
        toast({ tone: "success", title: scopedFolder ? t("collections.movedToast", "Moved") : t("collections.addedToast", "Added to folder"), subtitle: `${ids.length} → ${f ? f.name : ""}` });
      }
      setSel(new Set());
    } catch (e) {
      console.warn("filing failed:", e);
      toast({ tone: "danger", title: t("messaging.library.couldnTUpdateMessage", "Couldn't update message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") });
    }
  };
  /** Bulk Move to = the file-system verb: ends up in exactly that folder. */
  const bulkMoveTo = async (target: string) => {
    const ids = Array.from(sel);
    setMoveAt(null);
    try {
      const byFolder = new Map<string, string[]>();
      ids.forEach((id) => col.foldersOf(id).forEach((fid) => { if (fid !== target) byFolder.set(fid, [...(byFolder.get(fid) || []), id]); }));
      for (const [fid, list] of byFolder) await col.setItems(fid, { remove: list });
      await col.setItems(target, { add: ids });
      const f = col.folders.find((x) => x.id === target);
      toast({ tone: "success", title: t("collections.movedToast", "Moved"), subtitle: `${ids.length} → ${f ? f.name : ""}` });
      setSel(new Set());
    } catch (e) {
      console.warn("bulk move failed:", e);
      toast({ tone: "danger", title: t("messaging.library.couldnTUpdateMessage", "Couldn't update message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") });
    }
  };
  const doDeleteFolder = async (mode: "keep" | "purge") => {
    if (!askDelete) return;
    setDelBusy(true);
    try {
      await col.remove(askDelete.id, mode);
      if (scope === askDelete.id) setScope("all");
      if (mode === "purge") store.reload();
      toast({ tone: "warn", title: t("collections.folderDeleted", "Folder deleted"), subtitle: askDelete.name });
      setAskDelete(null);
    } catch (e) {
      console.warn("delete folder failed:", e);
      toast({ tone: "danger", title: t("messaging.library.couldnTUpdateMessage", "Couldn't update message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") });
    } finally { setDelBusy(false); }
  };

  const dup = async (m: Row) => { try { const c = await store.duplicate(m.id); toast({ tone: "success", title: t("messaging.library.messageDuplicated", "Message duplicated"), subtitle: c ? `"${c.name}" added as a draft.` : undefined }); } catch (e) { console.warn("duplicate message failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTDuplicateMessage", "Couldn't duplicate message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (m: Row) => { try { await store.remove(m.id); toast({ tone: "warn", title: t("messaging.library.messageDeleted", "Message deleted") }); } catch (e) { console.warn("delete message failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTDeleteMessage", "Couldn't delete message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } };
  const openMsg = (m: Row) => nav.go("messaging.compose", { id: m.id });

  const [flowMsgId, setFlowMsgId] = useState<string | null>(null);
  const flowMsg = flowMsgId ? store.get(flowMsgId) : null;

  const actions = (m: Row) => ({
    onView: () => nav.go("messaging.compose", { id: m.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("messaging.compose", { id: m.id }) : undefined,
    onDuplicate: canWrite ? () => dup(m) : undefined,
    moreHeader: m.name,
    moreItems: [
      ...(canWrite ? [{ icon: <Icon.folder size={14} />, label: `${t("collections.folders", "Folders")}\u2026`, onClick: (e?: unknown) => { const ev = e as MouseEvent | undefined; setLabelFor({ id: m.id, x: ev?.clientX ?? window.innerWidth / 2, y: ev?.clientY ?? 200 }); } }] : []),
      ...(canWrite ? [{ icon: <Icon.list size={14} />, label: t("messaging.library.addToFlow", "Add to flow…"), onClick: () => setFlowMsgId(m.id) }] : []),
      ...(canWrite && m.status === "draft" ? [{ icon: <Icon.check size={14} />, label: t("messaging.library.makeLive", "Make live"), onClick: async () => { const full = store.get(m.id); if (!full) return; try { await store.upsert({ ...full, status: "live" }); toast({ tone: "success", title: t("messaging.library.messageIsLive", "Message is live"), subtitle: i18nT("messaging.library.mNameIsAvailableToEventsAndFlows.icu", { value0: m.name }) }); } catch (e) { console.warn("make message live failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTMakeMessageLive", "Couldn't make message live"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } } }] : []),
      ...(canWrite && m.status === "live" ? [{ icon: <Icon.edit size={14} />, label: t("messaging.library.backToDraft", "Back to draft"), onClick: async () => { const full = store.get(m.id); if (!full) return; try { await store.upsert({ ...full, status: "draft" }); toast({ tone: "info", title: t("messaging.library.backToDraft", "Back to draft"), subtitle: i18nT("messaging.library.mNameIsEditableAgainAndNoLongerUsableInFlows.icu", { value0: m.name }) }); } catch (e) { console.warn("revert message to draft failed:", e); toast({ tone: "danger", title: t("messaging.library.couldnTUpdateMessage", "Couldn't update message"), subtitle: t("messaging.library.retryInAMoment", "Retry in a moment.") }); } } }] : []),
      ...(canWrite ? ["divider" as const, { icon: <Icon.trash size={14} />, label: t("common.actions.delete", "Delete"), danger: true, onClick: () => del(m) }] : []),
    ],
  });

  const statusTabs = [
    { id: "all", label: t("messaging.library.all", "All"), count: tabCounts.all },
    { id: "live", label: t("common.status.live", "Live"), count: tabCounts.live },
    { id: "draft", label: t("messaging.library.drafts", "Drafts"), count: tabCounts.draft },
  ];

  const toggleSel = (id: string, on: boolean) => setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
  const dragProps = (m: Row) => canWrite ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      const ids = sel.has(m.id) ? Array.from(sel) : [m.id];
      e.dataTransfer.setData("text/plain", JSON.stringify(ids));
      e.dataTransfer.effectAllowed = "move";
    },
  } : {};
  const folderChips = (id: string) => {
    if (scope !== "all" && scope !== "unfiled") return null;
    const fs = col.foldersOf(id).map((fid) => col.folders.find((f) => f.id === fid)).filter(Boolean) as Folder[];
    if (!fs.length) return null;
    return (
      <span style={{ display: "inline-flex", gap: 4, marginLeft: 8 }}>
        {fs.slice(0, 2).map((f) => <span key={f.id} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 600, color: T.soft, background: T.n6, borderRadius: 999, padding: "2px 7px" }}><Icon.folder size={9} />{f.name}</span>)}
        {fs.length > 2 ? <span style={{ fontSize: 10.5, color: T.soft, alignSelf: "center" }}>+{fs.length - 2}</span> : null}
      </span>
    );
  };

  const scopeName = scope === "all" ? t("collections.allItems", "All messages") : scope === "unfiled" ? t("collections.unfiled", "Unfiled") : (col.folders.find((f) => f.id === scope)?.name ?? "");
  const treeProps = { col, scope, onScope: setScope, countFor, canWrite, onDropIds: dropIds, allLabel: t("collections.allItems", "All messages"), onAskDelete: setAskDelete };

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "messages", counts)} activeTab="messages">
      <PageHeader
        title={t("messaging.library.messaging", "Messaging")}
        subtitle={t("messaging.library.createAndMaintainEventMessagingAcrossEmailSmsWhatsappTiktokInstagramAnd", "Create and maintain event messaging across email, SMS, WhatsApp, TikTok, Instagram and Facebook.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("messaging.compose", scopedFolder ? { new: 1, folder: scopedFolder } : { new: 1 })}>{t("messaging.library.newMessage", "New message")}</Btn> : null}
        </>}
      />
      <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        {desktop ? <FolderTree {...treeProps} /> : null}
        <div style={{ flex: 1, minWidth: 0, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column" }}>
          {!desktop ? <FolderCards {...treeProps} parentOf={(s) => { const f = col.folders.find((x) => x.id === s); return f?.parentId ?? "all"; }} /> : null}
          <FolderBreadcrumb col={col} scope={scope} onScope={setScope} allLabel={t("collections.allItems", "All messages")} />
          <div style={{ padding: "10px 0 4px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Input full={false} leading={<Icon.search size={14} />} placeholder={t("messaging.library.findAMessage", "Find a message…")} value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} />
            <span style={{ display: "inline-flex", borderRadius: 8, border: `1px solid ${T.border}`, overflow: "hidden", marginLeft: 4 }}>
              {statusTabs.map((s) => (
                <button key={s.id} onClick={() => setTab(s.id)} style={{ padding: "7px 12px", border: "none", borderRight: s.id !== "draft" ? `1px solid ${T.border}` : "none", background: tab === s.id ? T.p4 : T.white, color: tab === s.id ? T.p1 : T.n2, fontWeight: 600, fontSize: 12.5, cursor: "pointer", fontFamily: T.font }}>{s.label} {s.count}</button>
              ))}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: T.soft }}>{rows.length} {t("messaging.library.of", "of")}{" "}{wide ? msgRows.length : tabCounts.all}</span>
          </div>
          {q.trim() && scope !== "all" && !wide ? (
            <div style={{ fontSize: 12.5, color: T.soft, padding: "2px 0 6px" }}>
              {t("collections.searchingIn", "Searching in")} <b style={{ color: T.n1 }}>{scopeName}</b> ·{" "}
              <button onClick={() => setWide(true)} style={{ border: "none", background: "transparent", color: T.p1, cursor: "pointer", fontFamily: T.font, fontSize: 12.5, padding: 0, fontWeight: 600 }}>{t("collections.searchEverywhere", "Search all messages")}</button>
            </div>
          ) : null}
          {sel.size ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", margin: "4px 0 6px", borderRadius: 10, background: T.p4, border: `1px solid ${T.p1}22` }}>
              <b style={{ fontSize: 13, color: T.p1 }}>{sel.size} {t("collections.selected", "selected")}</b>
              <Btn size="sm" variant="secondary" icon={<Icon.folder size={13} />} onClick={(e) => setMoveAt({ x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY })}>{t("collections.moveTo", "Move to")}…</Btn>
              {scopedFolder ? <Btn size="sm" variant="secondary" onClick={() => { void col.setItems(scopedFolder, { remove: Array.from(sel) }).then(() => setSel(new Set())); }}>{t("collections.removeFromFolder", "Remove from folder")}</Btn> : null}
              <span style={{ flex: 1 }} />
              <button onClick={() => setSel(new Set())} style={{ border: "none", background: "transparent", color: T.soft, cursor: "pointer", fontFamily: T.font, fontSize: 12.5 }}>{t("collections.clear", "Clear")}</button>
            </div>
          ) : null}

          <LoadState loading={store.loading} error={store.error} empty={rows.length === 0}
            noun={t("messaging.library.nounMessages", "messages")} onRetry={() => store.reload()} testid="messaging-load"
            emptyState={<div style={{ marginTop: 14 }}>
              <EmptyState variant="card" icon={<Icon.mail size={20} />}
                title={q.trim() ? t("messaging.library.emptyFilteredTitle", "No message matches that") : scope !== "all" ? t("messaging.library.emptyFolderTitle", "Nothing in this folder yet") : t("messaging.library.emptyTitle", "No messages yet")}
                body={q.trim() ? t("messaging.library.emptyFilteredBody", "Try a different search, or clear it to see everything.") : scope !== "all" ? t("messaging.library.emptyFolderBody", "Drag messages here, or use a message's Folders… action.") : t("messaging.library.emptyBody", "Messages you create appear here, ready to use in flows and campaigns.")}
                action={can("campaigns.write") && !q.trim() ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("messaging.compose", scopedFolder ? { new: 1, folder: scopedFolder } : { new: 1 })}>{t("messaging.library.newMessage", "New message")}</Btn> : null}
                secondaryAction={q.trim() ? <Btn variant="secondary" onClick={() => setQ("")}>{t("messaging.library.clearSearch", "Clear search")}</Btn> : null} />
            </div>}>
          {view === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16, marginTop: 14 }}>
              {rows.map((m) => (
                <Card key={m.id} {...dragProps(m)} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", outline: sel.has(m.id) ? `2px solid ${T.p1}` : "none" }} onClick={() => openMsg(m)}>
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        {canWrite ? <input type="checkbox" checked={sel.has(m.id)} onClick={(e) => e.stopPropagation()} onChange={(e) => toggleSel(m.id, e.target.checked)} style={{ accentColor: T.p1 }} /> : null}
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: T.n1 }}>{m.name}</div>
                      </div>
                      {msgStatusPill(m.status)}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>{m.channels.map((id) => <ChannelChip key={id} id={id} size="sm" />)}</div>
                    <div style={{ marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.soft, display: "flex", alignItems: "center" }}>
                      {m.updated}{m.sentLabel ? ` · ${m.sentLabel}` : ""}{folderChips(m.id)}
                    </div>
                  </div>
                  <CardActionBar {...actions(m)} />
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ padding: 0, marginTop: 14 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                    {canWrite ? <th style={{ ...msgTh, width: 34, paddingRight: 0 }}><input type="checkbox" aria-label="Select all" checked={rows.length > 0 && rows.every((r) => sel.has(r.id))} onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} style={{ accentColor: T.p1 }} /></th> : null}
                    <th style={msgTh}>{t("messaging.library.message", "Message")}</th><th style={msgTh}>{t("messaging.library.channels", "Channels")}</th><th style={msgTh}>{t("messaging.library.status", "Status")}</th><th style={msgTh}>{t("messaging.library.updated", "Updated")}</th><th style={{ ...msgTh, width: 140 }}>{t("messaging.library.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m, i) => (
                    <tr key={m.id} {...dragProps(m)} onClick={() => openMsg(m)} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: sel.has(m.id) ? T.p4 : "transparent" }}>
                      {canWrite ? <td style={{ ...msgTd, width: 34, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.has(m.id)} onChange={(e) => toggleSel(m.id, e.target.checked)} style={{ accentColor: T.p1 }} /></td> : null}
                      <td style={{ ...msgTd, color: T.n1, fontWeight: 600 }}><div style={{ display: "flex", alignItems: "center" }}>{m.name}{folderChips(m.id)}</div></td>
                      <td style={msgTd}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{m.channels.map((id) => <span key={id} title={MSG_CHANNEL(id).name} style={{ display: "inline-flex" }}><ChGlyph id={id} size={15} color={MSG_CHANNEL(id).hue} /></span>)}</div></td>
                      <td style={msgTd}>{msgStatusPill(m.status)}</td>
                      <td style={{ ...msgTd, color: T.soft }}>{m.updated}</td>
                      <td style={msgTd} onClick={(e) => e.stopPropagation()}><RowActions {...actions(m)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
          </LoadState>
          <ExplainerFooter>
            <b>{t("messaging.library.oneMessageManyChannels", "One message, many channels.")}</b> {t("messaging.library.pickThePlatformsAMessageGoesOutOnAndTailorTheContent", "Pick the platforms a message goes out on and tailor the content per channel. This is an early sample — sending, channel connections and analytics come next.")}</ExplainerFooter>
        </div>
      </div>
      {flowMsg ? <AddToFlowModal message={flowMsg} onClose={() => setFlowMsgId(null)} /> : null}
      {labelFor ? (
        <FolderPickerPopover col={col} at={labelFor} mode="labels" title={t("collections.folders", "Folders")}
          checked={new Set(col.foldersOf(labelFor.id))} onClose={() => setLabelFor(null)}
          onToggle={(fid, on) => { void col.setItems(fid, on ? { add: [labelFor.id] } : { remove: [labelFor.id] }); }} />
      ) : null}
      {moveAt ? (
        <FolderPickerPopover col={col} at={moveAt} mode="move" title={`${t("collections.moveTo", "Move to")}…`} onClose={() => setMoveAt(null)} onPick={(fid) => void bulkMoveTo(fid)} />
      ) : null}
      {askDelete ? (
        <DeleteFolderModal folder={askDelete} busy={delBusy}
          count={msgRows.filter((m) => col.rollup(askDelete.id).has(m.id)).length}
          onClose={() => setAskDelete(null)} onKeep={() => void doDeleteFolder("keep")} onPurge={() => void doDeleteFolder("purge")} />
      ) : null}
    </Screen>
  );
}
